import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../../../provider/AuthProvider";
import { ROUTE_NAMES } from "../../../routes/routeConstants";
import SheetLoading from "./SheetLoading";

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** If true, only authenticated users can access this route */
  requireAuth?: boolean;
  /** If specified, only users with this role can access */
  allowedRoles?: ("employee" | "employer")[];
  /** If true, redirects authenticated users away (for login/register pages) */
  redirectIfAuthenticated?: boolean;
  /** Where to redirect authenticated users (default: role-based dashboard) */
  redirectTo?: string;
}

/**
 * ProtectedRoute component for handling authentication and authorization
 *
 * Usage examples:
 * - Public page: <ProtectedRoute>{children}</ProtectedRoute>
 * - Auth required: <ProtectedRoute requireAuth>{children}</ProtectedRoute>
 * - Role specific: <ProtectedRoute requireAuth allowedRoles={["employer"]}>{children}</ProtectedRoute>
 * - Guest only: <ProtectedRoute redirectIfAuthenticated>{children}</ProtectedRoute>
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAuth = false,
  allowedRoles,
  redirectIfAuthenticated = false,
  redirectTo,
}) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  // But NOT for login/register pages - they handle their own loading state
  if (loading && !redirectIfAuthenticated) {
    return <SheetLoading text="Loading…" />;
  }

  // Get the appropriate dashboard based on user role
  const getDashboardRoute = () => {
    if (!user) return ROUTE_NAMES.PAGES.LOGIN;
    return user.role === "employer"
      ? ROUTE_NAMES.PAGES.EMPLOYER_DASHBOARD
      : ROUTE_NAMES.PAGES.JOB_SEEKER_DASHBOARD;
  };

  // Handle guest-only pages (login, register)
  if (redirectIfAuthenticated && user) {
    const destination = redirectTo || getDashboardRoute();
    return <Navigate to={destination} replace />;
  }

  // Handle protected routes
  if (requireAuth) {
    // User not authenticated - redirect to login
    if (!user) {
      return (
        <Navigate
          to={ROUTE_NAMES.PAGES.LOGIN}
          state={{ from: location.pathname }}
          replace
        />
      );
    }

    // Check email verification
    if (!user.isEmailVerified) {
      return (
        <Navigate
          to={ROUTE_NAMES.PAGES.VERIFY_EMAIL}
          state={{ email: user.email, role: user.role }}
          replace
        />
      );
    }

    // Check onboarding completion — but never redirect to the onboarding page
    // if we're already there, otherwise the ProtectedRoute wrapping the
    // onboarding route re-fires this redirect and the page never renders.
    if (!user.onboardingCompleted) {
      const onboardingRoute =
        user.role === "employer"
          ? ROUTE_NAMES.PAGES.EMPLOYER_ONBOARDING
          : ROUTE_NAMES.PAGES.EMPLOYEE_ONBOARDING;
      if (location.pathname !== onboardingRoute) {
        return <Navigate to={onboardingRoute} replace />;
      }
    }

    // Check role authorization
    if (allowedRoles && !allowedRoles.includes(user.role)) {
      // Redirect to appropriate dashboard if role not allowed
      return <Navigate to={getDashboardRoute()} replace />;
    }
  }

  // Render children for public routes or authorized access
  return <>{children}</>;
};

export default ProtectedRoute;
